/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import i18nContext from 'components/i18n-context';
import { ReactComponent as PhoneIcon } from 'assets/phone-icon.svg';
import { AuthorizationBackButton } from 'components/common/AuthorizationBackButton/AuthorizationBackButton';
import { ConfirmationModalHeader } from 'components/common/ConfirmationModal/ConfirmationModalHeader';
import { getConfirmationSubtitle } from 'components/common/ConfirmationModal/getConfirmationSubtitle';
import Loader from 'components/common/Loader';
import Button from 'uikit/Button/Button';
import { ConfirmCodeWrapper } from 'components/common/ConfirmCodeWrapper/ConfirmCodeWrapper';
import { CONFIRMATION_HANDLE_TYPES, SMS_ERROR_CODE_DISABLE, TWO_FACTOR_AUTH_TYPES } from 'components/common/constants';
import { getTimerDurationSecondsLeft } from 'components/utils';
import ComplexTranslate from 'components/ComplexTranslate';
import { getErrorMessageForAlert } from 'services/utils';
import { getAvailableConfirmationTypes } from 'services/authUtils';
import registrationStore from 'stores/registrationStore';
import {
  getUnavailableConfirmationTypes,
  hasSwitchableConfirmationMethod
} from 'components/common/ActionConfirmation/components/SwitchConfirmationMethodScreen';
import './Confirmations.scss';

const SMS_CODE_LENGTH = 6;

/**
 * TRN-7254: despite the filename/component name (kept to limit the size of this change — it's
 * still exactly the "enter a code to confirm login/password-recovery/registration" screen), this
 * now renders the new modal for every TWO_FACTOR_AUTH_TYPES value ActionConfirmation/index.js
 * hands it, not just SMS/WhatsApp/Email — including Google Authenticator and push notifications,
 * which used to render through their own old, unstyled Google2FA/PushNotification2FA components
 * (deleted; see git history if that behavior is ever needed again).
 */
const Sms = ({
  type = TWO_FACTOR_AUTH_TYPES.SMS,
  sms,
  handleSms,
  resendCode,
  phone,
  error,
  isRegistration,
  isForgotPassword,
  authStore,
  passwordRecoveryStore,
  onClose
}) => {
  const i18n = useContext(i18nContext);
  const [showCountDown, setShowCountDown] = useState(false);
  const [alertState, setAlertState] = useState({ type: '', message: '' });
  const [isDisabled, setIsDisabled] = useState(false);
  const [code, setCode] = useState('');
  const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL;
  const isSubmitting = (isForgotPassword ? passwordRecoveryStore : authStore).isLoading;

  const handleSwitchToAnotherMethod = () =>
    isForgotPassword
      ? passwordRecoveryStore.setIsShowSwitchConfirmationMethodScreen(true)
      : authStore.setIsShowSwitchConfirmationMethodScreen(true);

  useEffect(() => {
    if (SMS_ERROR_CODE_DISABLE.includes(error?.code)) {
      setIsDisabled(true);
      handleSms(sms, true);
    }
    if (error) {
      setAlertState({ type: 'warning', message: getErrorMessageForAlert(i18n, error) });
      // Drop the stale (wrong) code so Confirm doesn't stay enabled and re-submit it as-is.
      setCode('');
    }
  }, [error]);

  useEffect(() => {
    // Push notifications don't have a "resend" concept (see isPushNotification below) — showing
    // the "code resent in Xs" countdown for them would be misleading, so skip it for that type.
    if (type !== TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION) {
      setShowCountDown(true);
    }
  }, []);

  useEffect(() => {
    const store = isForgotPassword ? passwordRecoveryStore : authStore;
    const expiresAt = store.confirmationExpiresAt;
    if (!expiresAt) return;

    const secondsLeft = getTimerDurationSecondsLeft(expiresAt);

    const timerId = setTimeout(() => {
      setIsDisabled(true);
      setAlertState({ type: 'warning', message: i18n.getMessage('error.SECURITY_CODE_EXPIRED') });
    }, secondsLeft * 1000);

    return () => clearTimeout(timerId);
  }, [isForgotPassword ? passwordRecoveryStore.confirmationExpiresAt : authStore.confirmationExpiresAt]);

  const handleResendCode = async () => {
    setShowCountDown(true);
    await resendCode();
    isDisabled && setIsDisabled(false);
    setCode('');
    setAlertState({ type: 'success', message: i18n.getMessage('pin.alert.sent') });
  };

  const onTimerEnd = () => {
    setShowCountDown(false);
  };

  // TRN-7254: the new design has an explicit Confirm button instead of auto-submitting the
  // instant all 6 digits are typed (matching how PopUpInternalActionConfirmationScheme's
  // already-modal flow works) — collect the code as the user types (ON_CHANGE), only call the
  // handleSms callback (the thing that actually submits) on Confirm.
  const onCodeChange = (value) => {
    setCode(value);
    if (alertState.type === 'warning') {
      setAlertState({ type: '', message: '' });
    }
  };

  const isCodeComplete = code.length === SMS_CODE_LENGTH;

  const handleConfirm = () => {
    if (!isCodeComplete || isDisabled || isSubmitting) return;
    handleSms(code);
  };

  const destination = phone ? <strong>{phone}</strong> : null;
  const isPushNotification = type === TWO_FACTOR_AUTH_TYPES.PUSH_NOTIFICATION;
  // TRN-7254 bugfix: "Try another method" must only show when the customer actually has another
  // verification method configured — not unconditionally for every login. TRN-7491: and one the switch
  // screen can actually list (same unavailable types as ActionConfirmation/index.js passes it).
  const hasAlternativeConfirmationMethod = hasSwitchableConfirmationMethod(
    type,
    getAvailableConfirmationTypes(),
    getUnavailableConfirmationTypes(error)
  );

  if (isRegistration) {
    // TRN-7254: registration renders this inline inside its own .registration-form (no
    // ConfirmationModal/PopUp around it — see Registration/User/index.js), so it keeps its
    // pre-redesign layout (own heading/icon, auto-submit on complete, no Confirm button) instead
    // of picking up the new modal-only chrome below. This screen isn't in the Figma scope for
    // this ticket and hasn't had a visual pass, so it's intentionally left as it was.
    const onComplete = (smsCode) => {
      handleSms(smsCode);
      setAlertState({ type: '', message: '' });
    };

    return (
      <div className={'authorization-form'} id={'form1'}>
        <PhoneIcon className={'svg-phone-icon registration-icon'} />
        <h1 className={'security-code-title'}>{i18n.getMessage('sms.title')}</h1>
        <ConfirmCodeWrapper
          type={type}
          PhoneComponent={destination}
          isDisabled={isDisabled}
          error={error}
          onComplete={onComplete}
          showCountDown={showCountDown}
          handleResendCode={handleResendCode}
          onTimerEnd={onTimerEnd}
          alertState={alertState}
          isSwitchToAnotherConfirmationMethod={false}
        />
        <div className={'back-to-previous-step'}>
          <ComplexTranslate
            text={'register.backToUserRegistration'}
            interpolation={{
              previousStep: (
                <button
                  className={'unset link-normal'}
                  onClick={() => {
                    registrationStore.changePhoneNumber();
                  }}
                >
                  {i18n.getMessage('register.previousStep')}
                </button>
              )
            }}
          />
        </div>
        <AuthorizationBackButton type={'login'} />
      </div>
    );
  }

  return (
    <>
      <ConfirmationModalHeader
        title={i18n.getMessage('sms.title')}
        subtitle={getConfirmationSubtitle(type, phone, i18n)}
        onClose={onClose}
      />
      <div className={'confirmation-modal__body'}>
        <ConfirmCodeWrapper
          type={type}
          PhoneComponent={destination}
          isDisabled={isDisabled}
          error={error}
          onComplete={onCodeChange}
          confirmationHandleType={CONFIRMATION_HANDLE_TYPES.ON_CHANGE}
          showCountDown={showCountDown}
          handleResendCode={handleResendCode}
          onTimerEnd={onTimerEnd}
          alertState={alertState}
          isSwitchToAnotherConfirmationMethod={!isForgotPassword && hasAlternativeConfirmationMethod}
          handleSwitchToAnotherMethod={handleSwitchToAnotherMethod}
          hideDescription
        />
        {/*
          Push notification confirms itself via background polling (see ActionConfirmation/index.js)
          — there's no code to type and nothing for this button to submit.
        */}
        {!isPushNotification && (
          <Button
            type={'primary'}
            size={'large'}
            fullWidth
            onClick={handleConfirm}
            isDisabled={!isCodeComplete || isDisabled || isSubmitting}
          >
            {isSubmitting ? <Loader isText={false} /> : i18n.getMessage('sms.btn.confirm')}
          </Button>
        )}
        {isForgotPassword && (
          <p className={'forgot-password-info-text'}>
            {i18n.getMessage('sms.forgotPassword.info2')}
            {supportEmail && (
              <a className={'forgot-password-info-link'} href={`mailto:${supportEmail}`}>
                {supportEmail}
              </a>
            )}
          </p>
        )}
      </div>
    </>
  );
};

Sms.propTypes = {
  type: PropTypes.string,
  sms: PropTypes.string.isRequired,
  phone: PropTypes.string,
  handleSms: PropTypes.func.isRequired,
  resendCode: PropTypes.func.isRequired,
  error: PropTypes.any,
  isRegistration: PropTypes.bool,
  isForgotPassword: PropTypes.bool,
  authStore: MobXPropTypes.observableObject,
  passwordRecoveryStore: MobXPropTypes.observableObject,
  onClose: PropTypes.func
};

export default inject('authStore', 'passwordRecoveryStore')(observer(Sms));
